'use client';

import { LinkStyled } from '@components/common';
import { useTranslation } from 'next-i18next';
import { usePathname } from '@hooks/next-routing-wrappers';

/**
 * @description Wallet Navigation Paths
 */
const walletPaths = [
  { // Services — label matches the main navbar services link
    path: '/wallet/services',
    userPathParams: '',
    label: 'header.label.pricing',
    tab: '',
  },
  { // Transactions
    path: '/wallet/transactions',
    userPathParams: '',
    label: 'global.label.transactions',
    tab: '',
  },
  { // Invoices
    path: '/wallet/orders',
    userPathParams: '',
    label: 'account.consumption.invoices',
    hideRoleFromUrl: true,
    tab: '',
  },
  { // Billing data
    path: '/wallet/billing-data',
    userPathParams: '',
    label: 'account.consumption.billing_data',
    tab: '',
  },
  { // Neuro earn
    path: '/neuro',
    userPathParams: '?tab=0',
    label: 'wallet.label.abount_neuro',
    tab: '0',
  },
  // { // Neuro spend
  //   path: '/neuro',
  //   userPathParams: '&tab=1',
  //   label: 'wallet.label.spend',
  //   roles: ['user'],
  //   tab: '1',
  // },
];

/**
 * @description Check if the current URL is the same as the object path.
 * - `currPath` - The current path.
 * - `tab` - The current tab.
 * - `objPath` - The object path.
 * - `objTab` - The object tab.
 */
const isCurrentUrl = (currPath: string | null, tab: string | undefined, objPath: string, objTab: string) => {
  const isCurrent = Boolean(currPath && currPath.includes(objPath.slice(0, -1)));
  const isTab = tab === objTab;
  const isZeroTab = objTab === '0' && !tab && isCurrent;
  return isCurrent && (isTab || isZeroTab);
};

/** Props */
interface WalletNavProps {
  searchParams: BjSearchParams;
}

/**
 * @description Wallet Navigation. Used to navigate between wallet sections.
 * - `searchParams`: *BjSearchParams* - Optional search params to pass to the transactions URL.
 * - `isAuth`: *boolean* - Whether the user is authenticated.
 */
export const WalletNav = (props: WalletNavProps) => {
  // Destructure props
  const {
    searchParams,
  } = props;

  // Hooks
  const { t } = useTranslation('common');
  const currentPath = usePathname();

  // Handle search params
  const tab = searchParams?.tab as string ?? '';

  // Helpers — segmented-control tab bar: a soft track with the active tab as a raised white pill.
  // Each tab keeps its label on one line (whitespace-nowrap); the track wraps to a second row on
  // narrow screens (flex-wrap) so it stays within the page and never causes horizontal scroll.
  const linkCommonClass = 'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-4 py-2 transition-all duration-200 md:px-6';
  const containerClass = 'inline-flex max-w-full flex-wrap justify-center gap-1 rounded-xl bg-surface p-1 text-sm font-medium';
  const buttonClass = {
    active: 'bg-surface-150 text-ink shadow-sm',
    inactive: 'text-ink-medium hover:bg-surface-150 hover:text-ink',
  };


  // Render component
  // *************************
  // Same centered container + horizontal margins as the page's other sections (max-w-5xl,
  // mx-auto, inside the page `.container`). Centered tab bar that wraps instead of scrolling.
  return (
    <div className="mx-auto flex w-full max-w-5xl justify-center pt-3 sm:pt-5 print:hidden">
      <div className={containerClass}>
        {
          walletPaths.map((elem) => (
            <LinkStyled
              key={elem.label}
              href={`${elem.path}${elem.userPathParams ? elem.userPathParams : ''}`}
              styling="none"
              className={`${linkCommonClass} ${isCurrentUrl(currentPath, tab, elem.path, elem.tab) ? buttonClass.active : buttonClass.inactive}`}
            >
              { t(elem.label) }
            </LinkStyled>
          ))
        }
      </div>
    </div>
  );
};
